import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Clock, XCircle, Phone, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentConfirmation = () => {
  const [searchParams] = useSearchParams();
  const quoteRef = searchParams.get('quote');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 24; // 2 minutes max (24 x 5 seconds)

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!quoteRef) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/api/payments/by-quote/${quoteRef}`);
        if (response.data.success) {
          setPaymentData(response.data.data);
          const paymentStatus = response.data.data.status;
          if (paymentStatus === 'paid') {
            setStatus('success');
          } else if (paymentStatus === 'failed' || paymentStatus === 'canceled' || paymentStatus === 'expired') {
            setStatus('failed');
          } else {
            setStatus('pending');
          }
        } else {
          setStatus('pending');
        }
      } catch (error) {
        // If payment not found, it might still be processing
        setStatus('pending');
      }
      
      setPollCount(prev => prev + 1);
    };

    checkPaymentStatus();
    
    // Poll for status updates (max 2 minutes)
    const interval = setInterval(() => {
      if (pollCount < maxPolls && status !== 'success' && status !== 'failed') {
        checkPaymentStatus();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [quoteRef, pollCount, status]);

  const renderContent = () => {
    switch (status) {
      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-slate-800 mb-4">
              Payment Successful!
            </h1>
            <p className="text-lg text-slate-600 mb-2">
              Thank you for your deposit payment.
            </p>
            {paymentData && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 inline-block">
                <p className="text-green-800">
                  <strong>Amount Paid:</strong> £{parseFloat(paymentData.deposit_amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-green-700 text-sm mt-1">
                  Quote Reference: {paymentData.quote_reference}
                </p>
              </div>
            )}
            <p className="text-slate-600 mb-8">
              We've received your deposit and will be in touch shortly to confirm your installation date.
              A confirmation email has been sent to your email address.
            </p>
          </div>
        );

      case 'pending':
      case 'processing':
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock className="w-12 h-12 text-amber-600 animate-pulse" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-slate-800 mb-4">
              {pollCount > 6 ? 'Still Processing...' : 'Processing Payment...'}
            </h1>
            <p className="text-lg text-slate-600 mb-4">
              {pollCount > 6 
                ? "This is taking longer than expected. Your payment may still be processing."
                : "Please wait while we confirm your payment with our payment provider."
              }
            </p>
            {pollCount > 12 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-amber-800 text-sm">
                  <strong>Payment taking too long?</strong><br/>
                  If you completed the payment, it may take a few moments to process. 
                  You can close this page - we'll send a confirmation email once your payment is confirmed.
                </p>
              </div>
            )}
            <p className="text-sm text-slate-500">
              Quote Reference: {quoteRef}
            </p>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-slate-800 mb-4">
              Payment Not Completed
            </h1>
            <p className="text-lg text-slate-600 mb-6">
              We couldn't confirm your payment. This might happen if:
            </p>
            <ul className="text-slate-600 text-left max-w-md mx-auto mb-8 space-y-2">
              <li>• The payment was cancelled</li>
              <li>• There was an issue with your payment method</li>
              <li>• The payment is still being processed</li>
            </ul>
            <p className="text-slate-600 mb-8">
              If you believe this is an error, please contact us.
            </p>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-sage-500 border-t-transparent rounded-full mx-auto mb-6"></div>
            <p className="text-slate-600">Loading payment status...</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-50 to-white py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            {renderContent()}

            <div className="mt-8 pt-8 border-t border-slate-200">
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/">
                  <Button variant="outline" className="flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    Return Home
                  </Button>
                </Link>
                <a href="https://wa.me/447378415338">
                  <Button className="bg-green-600 hover:bg-green-700 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contact Us
                  </Button>
                </a>
              </div>
            </div>
          </div>

          <div className="text-center mt-8 text-slate-500 text-sm">
            <p>Aston Lily Shutters • Family Business Since 2018</p>
            <p>10 Year Structural Warranty on All Products</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmation;
